import { Component, OnDestroy, ViewChild } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { MatSidenavContainer } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { MenuItem } from 'src/app/models/menu-item.model';
import { DOC_NAV_MENU } from 'src/app/navigation/doc-nav.config';

@Component({
  selector: 'app-doc-layout',
  templateUrl: './doc-layout.component.html',
  styleUrls: ['./doc-layout.component.scss'],
})
export class DocLayoutComponent implements OnDestroy {
  @ViewChild('sidenavContainer') sidenavContainer: MatSidenavContainer;

  readonly menuList: MenuItem[] = DOC_NAV_MENU;
  readonly searchableEntries: Array<{ label: string; route: string }> = this.buildSearchEntries(DOC_NAV_MENU);
  filteredSearchableEntries: Array<{ label: string; route: string }> = this.searchableEntries.slice(0, 8);
  searchQuery = '';
  searchFeedback = '';
  private feedbackTimeout: ReturnType<typeof setTimeout> | null = null;

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map((result) => result.matches),
    shareReplay()
  );

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    private readonly router: Router
  ) {}

  onActivate(): void {
    this.sidenavContainer?.scrollable?.scrollTo({ left: 0, top: 0 });
  }

  onSearchInput(value: string): void {
    this.searchQuery = value;
    this.updateFilteredEntries(value);
    this.clearSearchFeedback();
  }

  onSearchOptionSelected(label: string): void {
    this.searchQuery = label;
    const match = this.searchableEntries.find((entry) => entry.label === label);
    if (!match) {
      this.showSearchFeedback('No se encontró una ruta válida.');
      return;
    }

    this.navigateToEntry(match);
  }

  goToSearchResult(): void {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      this.showSearchFeedback('Escribe algo para buscar.');
      return;
    }

    const exactMatch = this.searchableEntries.find((entry) => entry.label.toLowerCase() === query);
    const partialMatch =
      exactMatch ??
      this.searchableEntries.find(
        (entry) => entry.label.toLowerCase().includes(query) || entry.route.toLowerCase().includes(query)
      );

    if (!partialMatch) {
      this.showSearchFeedback('Sin resultados para esa búsqueda.');
      return;
    }

    this.navigateToEntry(partialMatch);
  }

  ngOnDestroy(): void {
    if (this.feedbackTimeout) {
      clearTimeout(this.feedbackTimeout);
    }
  }

  private buildSearchEntries(items: MenuItem[], parentLabel = ''): Array<{ label: string; route: string }> {
    const entries: Array<{ label: string; route: string }> = [];

    for (const item of items) {
      const mergedLabel = parentLabel ? `${parentLabel} / ${item.label}` : item.label;
      if (item.route) {
        entries.push({ label: mergedLabel, route: item.route });
      }

      if (item.children?.length) {
        entries.push(...this.buildSearchEntries(item.children, mergedLabel));
      }
    }

    return entries;
  }

  private navigateToEntry(entry: { label: string; route: string }): void {
    this.searchQuery = entry.label;
    this.clearSearchFeedback();
    this.router.navigateByUrl(`/${entry.route}`);
  }

  private updateFilteredEntries(rawQuery: string): void {
    const query = rawQuery.trim().toLowerCase();
    if (!query) {
      this.filteredSearchableEntries = this.searchableEntries.slice(0, 8);
      return;
    }

    this.filteredSearchableEntries = this.searchableEntries
      .filter(
        (entry) => entry.label.toLowerCase().includes(query) || entry.route.toLowerCase().includes(query)
      )
      .slice(0, 8);
  }

  private showSearchFeedback(message: string): void {
    this.searchFeedback = message;
    if (this.feedbackTimeout) {
      clearTimeout(this.feedbackTimeout);
    }
    this.feedbackTimeout = setTimeout(() => {
      this.searchFeedback = '';
      this.feedbackTimeout = null;
    }, 2200);
  }

  private clearSearchFeedback(): void {
    this.searchFeedback = '';
    if (this.feedbackTimeout) {
      clearTimeout(this.feedbackTimeout);
      this.feedbackTimeout = null;
    }
  }
}
